import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon,
  BrokenImage as BrakingIcon,
  Timeline as SuspensionIcon,
  Settings as SettingsIcon,
  LocalGasStation as EngineIcon,
  AirportShuttle as BodyworkIcon,
  Security as SafetyIcon,
  Memory as ElectronicsIcon,
  SettingsApplications as PartsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { car as carApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import { format } from 'date-fns';

const CarReplacementDetailPage = () => {
  const { carId, replacementId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['cars', 'common']);
  const { user } = useAuth();
  
  const [car, setCar] = useState(null);
  const [replacement, setReplacement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [replacementTypes, setReplacementTypes] = useState([]);
  const [loadingReplacementTypes, setLoadingReplacementTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editFormData, setEditFormData] = useState({
    title: '',
    type: '',
    subtype: '',
    date: '',
    notes: ''
  });
  
  useEffect(() => {
    fetchCarDetails();
    fetchReplacementTypes();
  }, [carId, replacementId]);
  
  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      const data = await carApi.getById(carId);
      setCar(data.car);
      
      // Find the specific replacement
      const foundReplacement = data.car.replacements.find(r => r._id === replacementId);
      if (foundReplacement) {
        setReplacement(foundReplacement);
        
        // Initialize edit form data
        setEditFormData({
          title: foundReplacement.title || '',
          type: foundReplacement.type || '',
          subtype: foundReplacement.subtype || '',
          date: foundReplacement.date ? format(new Date(foundReplacement.date), 'yyyy-MM-dd') : '',
          notes: foundReplacement.notes || ''
        });
      } else {
        setError('Replacement not found');
        toast.error('Replacement not found');
      }
    } catch (error) {
      console.error('Error fetching car details:', error);
      setError('Error loading car details');
      toast.error('Error loading car details');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReplacementTypes = async () => {
    try {
      setLoadingReplacementTypes(true);
      const types = await carApi.getReplacementTypes();
      setReplacementTypes(types);
    } catch (error) {
      console.error('Error fetching replacement types:', error);
      toast.error('Error fetching replacement types');
    } finally {
      setLoadingReplacementTypes(false);
    }
  };
  
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
    
    // Reset subtype if type changes
    if (name === 'type') {
      setEditFormData(prev => ({
        ...prev,
        subtype: ''
      }));
    }
  };
  
  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      
      // Validate form data
      if (!editFormData.title || !editFormData.type || !editFormData.subtype) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      // Update the replacement
      await carApi.updateReplacement(carId, replacementId, editFormData);
      toast.success('Replacement updated successfully');
      
      // Refresh data
      fetchCarDetails();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating replacement:', error);
      toast.error('Error updating replacement');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteReplacement = async () => {
    try {
      setSaving(true);
      await carApi.deleteReplacement(carId, replacementId);
      toast.success('Replacement deleted successfully');
      navigate(`/cars/${carId}`);
    } catch (error) {
      console.error('Error deleting replacement:', error);
      toast.error('Error deleting replacement');
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };
  
  const getAvailableSubtypes = () => {
    if (!editFormData.type) return [];
    
    const selectedType = replacementTypes.find(type => type.value === editFormData.type);
    return selectedType ? selectedType.subtypes : [];
  };
  
  const getReplacementTypeIcon = (type) => {
    switch (type) {
      case 'consumables':
        return <SpeedIcon color="primary" fontSize="large" />;
      case 'braking':
        return <BrakingIcon color="error" fontSize="large" />;
      case 'suspension_chassis':
        return <SuspensionIcon color="secondary" fontSize="large" />;
      case 'drivetrain_transmission':
        return <SettingsIcon color="warning" fontSize="large" />;
      case 'engine_powertrain':
        return <EngineIcon color="success" fontSize="large" />;
      case 'aero_bodywork':
        return <BodyworkIcon color="info" fontSize="large" />;
      case 'safety':
        return <SafetyIcon color="error" fontSize="large" />;
      case 'electronics_data':
        return <ElectronicsIcon color="primary" fontSize="large" />;
      default:
        return <PartsIcon fontSize="large" />;
    }
  };
  
  const getTypeLabel = (type) => {
    const foundType = replacementTypes.find(t => t.value === type);
    return foundType ? foundType.label : type;
  };
  
  const getSubtypeLabel = (type, subtype) => {
    const foundType = replacementTypes.find(t => t.value === type);
    if (!foundType) return subtype;
    
    const foundSubtype = foundType.subtypes.find(s => s.value === subtype);
    return foundSubtype ? foundSubtype.label : subtype;
  };
  
  const isCarOwner = () => {
    return user && car && car.user && (user._id === car.user._id || user.isAdmin);
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error || !car || !replacement) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(`/cars/${carId}`)}
            sx={{ mb: 2 }}
          >
            Back to Car
          </Button>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'Replacement not found'}
          </Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box mt={4}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(`/cars/${carId}`)}
          sx={{ mb: 2 }}
        >
          Back to Car
        </Button>
        
        <Paper elevation={3}>
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box display="flex" alignItems="center">
                {getReplacementTypeIcon(replacement.type)}
                <Typography variant="h4" component="h1" sx={{ ml: 2 }}>
                  {replacement.title}
                </Typography>
              </Box>
              
              {isCarOwner() && (
                <Box>
                  <IconButton 
                    color="primary" 
                    onClick={() => setEditDialogOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Car
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {car.name}
                </Typography>
                
                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                  Type
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {getTypeLabel(replacement.type)}
                </Typography>
                
                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                  Subtype
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {getSubtypeLabel(replacement.type, replacement.subtype)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {format(new Date(replacement.date), 'PPP')}
                </Typography>
                
                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                  Added By
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {replacement.addedBy?.name || 'Unknown'}
                </Typography>
                
                {replacement.notes && (
                  <>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2 }}>
                      Notes
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {replacement.notes}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Replacement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Title"
                fullWidth
                required
                value={editFormData.title}
                onChange={handleEditChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={editFormData.type}
                  onChange={handleEditChange}
                  label="Type"
                >
                  {loadingReplacementTypes ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    replacementTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!editFormData.type}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  name="subtype"
                  value={editFormData.subtype}
                  onChange={handleEditChange}
                  label="Subtype"
                >
                  {getAvailableSubtypes().map((subtype) => (
                    <MenuItem key={subtype.value} value={subtype.value}>
                      {subtype.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="date"
                label="Date"
                type="date"
                fullWidth
                required
                value={editFormData.date}
                onChange={handleEditChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={editFormData.notes}
                onChange={handleEditChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
            disabled={saving || !editFormData.title || !editFormData.type || !editFormData.subtype}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Replacement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the replacement "{replacement.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteReplacement} color="error" variant="contained">
            {saving ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CarReplacementDetailPage; 